import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";

const POINTS = [
  "Daily, product-level demand forecasts",
  "Built from your sales, weather, holidays and events",
  "Purchasing recommendations with the reasoning shown",
];

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    email: "", password: "", name: "", org_name: "My Cafe Group", store_name: "My Cafe",
    city: "Christchurch", pilot_code: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register(f);
      toast.success("Welcome to Optics");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      <div className="hidden md:flex bg-zinc-950 relative overflow-hidden flex-col justify-between p-12 text-white">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background: "radial-gradient(80% 60% at 20% 0%, rgba(255,255,255,0.12) 0%, rgba(9,9,11,0) 60%)",
          }}
        />
        <Link to="/" className="relative" aria-label="Optics home">
          <Logo dark wordmark="Optics Software" />
        </Link>
        <div className="relative">
          <h2 className="font-display font-semibold text-4xl leading-[1.1] max-w-md">
            See demand before it happens.
          </h2>
          <ul className="mt-8 space-y-3">
            {POINTS.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-zinc-300">
                <span className="h-5 w-5 rounded-full bg-white/10 grid place-items-center shrink-0">
                  <Check className="h-3 w-3 text-white" />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-xs text-zinc-500">Made for cafés</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white border border-zinc-200 rounded-2xl shadow-card p-8">
          <div className="md:hidden mb-6">
            <Link to="/" aria-label="Optics home">
              <Logo wordmark="Optics Software" />
            </Link>
          </div>
          <h1 className="font-display text-2xl font-semibold">{mode === "login" ? "Sign in" : "Create your account"}</h1>
          <p className="text-sm text-zinc-500 mt-1">{mode === "login" ? "Welcome back to Optics." : "Start forecasting in minutes."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <Field label="Your name" value={f.name} onChange={set("name")} testid="name" />
                <Field label="Café group name" value={f.org_name} onChange={set("org_name")} testid="org" />
                <Field label="Store name" value={f.store_name} onChange={set("store_name")} testid="store" />
                <Field label="City" value={f.city} onChange={set("city")} testid="city" placeholder="Christchurch" required />
                <Field label="Pilot code (optional)" value={f.pilot_code} onChange={set("pilot_code")} testid="pilot-code" placeholder="Invited by us? Enter your code" />
              </>
            )}
            <Field label="Email" type="email" value={f.email} onChange={set("email")} testid="email" required />
            <Field label="Password" type="password" value={f.password} onChange={set("password")} testid="password" required />
            <button data-testid="auth-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-zinc-900 text-white py-2.5 rounded-full font-medium hover:bg-zinc-700 transition-colors duration-200 disabled:opacity-60">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          {mode === "login" && (
            <Link to="/forgot-password" className="mt-4 block text-center text-sm text-zinc-600 hover:underline">Forgot your password?</Link>
          )}

          <div className="mt-6 text-sm text-zinc-500 text-center">
            {mode === "login" ? "New to Optics?" : "Already have an account?"}{" "}
            <button data-testid="toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-zinc-900 font-medium hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">{label}</span>
      <input
        {...props}
        data-testid={`input-${testid}`}
        className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
      />
    </label>
  );
}
